import {
  migrateProductHeightFromGround,
  withLegacyHeightFromGround,
} from '../../shared/productHeights';
import type { ProductTemplate } from '../../shared/types';
import type { BaserowRow } from './client';

/**
 * Pure mapping + diff logic between the app's ProductTemplate list and Baserow
 * rows. Kept free of Electron/Node so it can be unit-tested directly.
 *
 * Each product is stored with a few flat, human-readable columns (so the
 * Baserow grid is legible) plus a `data` column holding the full template JSON,
 * which is the source of truth on read-back.
 */

/** Map of app product id -> the Baserow row it lives in, with a content hash. */
export interface RowMapEntry {
  id: string;
  rowId: number;
  hash: string;
}

/**
 * Field values written to a Baserow row (with user_field_names=true).
 * `appVersion` is the app version that wrote the row, stored as text in the
 * `number` field (a dotted version like "0.6.0" needs a Text field, not Number).
 */
export function productToFields(p: ProductTemplate, appVersion = ''): Record<string, unknown> {
  return {
    product_id: p.id,
    // The product name lives in the table's primary field. Baserow reserves
    // names like "id", so the primary field is called "Nome".
    Nome: p.name,
    // `category` is plain text (sauna / steamroom / shower / heater / custom).
    category: p.category,
    // `version` is a Text field so it can hold a dotted version number.
    version: appVersion,
    // Whole-mm Number columns, for reading the grid: Baserow refuses 853.1.
    // `data` keeps the exact figures.
    width: Math.round(p.width),
    depth: Math.round(p.depth),
    color: p.color,
    description: p.description ?? '',
    data: JSON.stringify(withLegacyHeightFromGround(p)),
  };
}

/** Reconstruct a ProductTemplate from a row (prefer the JSON blob). */
export function rowToProduct(row: BaserowRow): ProductTemplate | null {
  const data = row.data;
  if (typeof data === 'string' && data.trim()) {
    try {
      const p = JSON.parse(data) as ProductTemplate;
      p.builtin = false; // cloud products are always user products
      // Read in the current shape, so its hash matches the app's copy and an
      // unedited product is never uploaded again.
      migrateProductHeightFromGround(p);
      return p;
    } catch {
      // fall through to the flat columns
    }
  }
  const id = row.product_id;
  if (id === undefined || id === null || id === '') return null;
  // Read the primary "Nome" field, falling back to legacy "name".
  const nome = row.Nome ?? row.name;
  return {
    id: String(id),
    name: typeof nome === 'string' ? nome : '',
    category: (row.category as ProductTemplate['category']) || 'custom',
    width: Number(row.width) || 0,
    depth: Number(row.depth) || 0,
    color: typeof row.color === 'string' ? row.color : '#888888',
    description: typeof row.description === 'string' ? row.description : undefined,
    properties: [],
    builtin: false,
  };
}

// Identity / bookkeeping fields that aren't meaningful in a change-log diff.
const DIFF_SKIP_KEYS = new Set(['id', 'builtin']);

export interface FieldDiff {
  changedFields: string[];
  before: Record<string, unknown>;
  after: Record<string, unknown>;
}

/**
 * Field-level diff between a product's previous state and its saved state — the
 * difference between what the edit screen showed when it opened and what was
 * saved. Compares each top-level field; a field counts as changed when its JSON
 * differs. `oldP` is undefined for a brand-new product (everything is "after").
 * The values are whole, drawings included, so a version can be rebuilt from the
 * log (src/shared/productHistory.ts); the writers compress a long one.
 */
export function diffProductFields(oldP: ProductTemplate | undefined, newP: ProductTemplate): FieldDiff {
  const keys = new Set<string>([...Object.keys(oldP ?? {}), ...Object.keys(newP)]);
  const changedFields: string[] = [];
  const before: Record<string, unknown> = {};
  const after: Record<string, unknown> = {};
  for (const k of keys) {
    if (DIFF_SKIP_KEYS.has(k)) continue;
    const ov = (oldP as unknown as Record<string, unknown> | undefined)?.[k];
    const nv = (newP as unknown as Record<string, unknown>)[k];
    if (JSON.stringify(ov) === JSON.stringify(nv)) continue;
    changedFields.push(k);
    if (ov !== undefined) before[k] = ov;
    if (nv !== undefined) after[k] = nv;
  }
  return { changedFields, before, after };
}

/** A whole product, drawings included, for logging a delete's `before`. */
export function snapshotForLog(p: ProductTemplate): Record<string, unknown> {
  return { ...p } as unknown as Record<string, unknown>;
}

/** Small, stable content hash (FNV-ish) so unchanged products skip a PATCH. */
export function hashProduct(p: ProductTemplate): string {
  const s = JSON.stringify(p);
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(16);
}
